import React, { useState } from 'react';
import { Minus, Search, Eye, AlertTriangle, CheckCircle } from 'lucide-react';
import { customerService } from '../../services/customerService';
import { rewardsService } from '../../services/rewardsService';
import { getRewardBatches } from '../../services/rewardsService';
import { formatters } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import { useRewards } from '../../hooks/useRewards';
import { useSlotMachine } from '../../hooks/useSlotMachine';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import SlotMachineAnimation from '../common/SlotMachineAnimation';

const ForceRedemption = () => {
  const { user } = useAuth();
  const { rewards, fetchRewards } = useRewards(user?.companyId);
  
  const [customerDocument, setCustomerDocument] = useState('');
  const [customer, setCustomer] = useState(null);
  const [selectedReward, setSelectedReward] = useState(null);
  const [customPoints, setCustomPoints] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingRedemption, setProcessingRedemption] = useState(null); // ID do prêmio sendo processado
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showQuickRedemptionModal, setShowQuickRedemptionModal] = useState(false);
  const [selectedQuickReward, setSelectedQuickReward] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [quickRedemptionBatches, setQuickRedemptionBatches] = useState([]);
  const [selectedQuickBatch, setSelectedQuickBatch] = useState('');
  const [loadingQuickBatches, setLoadingQuickBatches] = useState(false);
  const { animationData, showRewardAnimation, hideAnimation, resetAnimation } = useSlotMachine();

  React.useEffect(() => {
    if (user?.companyId) {
      fetchRewards(true); // Buscar apenas prêmios ativos
    }
  }, [user?.companyId]);

  const handleSearchCustomer = async () => {
    if (!customerDocument.trim()) {
      setError('Digite um CPF ou CNPJ para buscar');
      return;
    }

    // Limpar documento para validação
    const cleanDocument = customerDocument.replace(/[^\d]/g, '');
    
    if (cleanDocument.length === 0) {
      setError('Documento não pode estar vazio');
      return;
    }
    
    if (cleanDocument.length !== 11 && cleanDocument.length !== 14) {
      setError(`Documento deve ter 11 dígitos (CPF) ou 14 dígitos (CNPJ). Você digitou ${cleanDocument.length} dígitos.`);
      return;
    }

    setLoading(true);
    setError('');
    setCustomer(null);

    try {
      const customers = await customerService.getByDocument(cleanDocument);
      const foundCustomer = customers.find(c => c.companyId === user.companyId);
      
      if (!foundCustomer) {
        setError('Cliente não encontrado nesta empresa');
        return;
      }

      setCustomer(foundCustomer);
    } catch (err) {
      setError('Erro ao buscar cliente: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = async () => {
    if (!customer) return;

    try {
      setLoading(true);
      const { pointsService } = await import('../../services/pointsService');
      const history = await pointsService.getCustomerHistory(customer.id);
      setCustomerHistory(history);
      setShowHistoryModal(true);
    } catch (err) {
      setError('Erro ao carregar histórico: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Data não disponível';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  const handleQuickRedemption = async (reward) => {
    if (!customer || customer.points < reward.pointsRequired) {
      setError('Cliente não possui pontos suficientes para este prêmio');
      return;
    }

    // Carregar lotes disponíveis se for produto/aluguel com controle de estoque
    if (reward.id && reward.hasStockControl !== false && 
        (reward.type === 'product' || reward.type === 'rental')) {
      await loadQuickRedemptionBatches(reward.id);
    }

    // Mostrar modal de confirmação
    setSelectedQuickReward(reward);
    setShowQuickRedemptionModal(true);
  };

  const loadQuickRedemptionBatches = async (rewardId) => {
    setLoadingQuickBatches(true);
    try {
      const batches = await getRewardBatches(rewardId);
      setQuickRedemptionBatches(batches);
      
      // Selecionar o primeiro lote disponível automaticamente
      if (batches.length > 0) {
        setSelectedQuickBatch(batches[0].batchCode);
      }
    } catch (error) {
      console.error('Erro ao carregar lotes:', error);
      setQuickRedemptionBatches([]);
    } finally {
      setLoadingQuickBatches(false);
    }
  };

  const confirmQuickRedemption = async () => {
    if (!selectedQuickReward) return;

    // Validar seleção de lote apenas quando há lotes disponíveis para seleção
    if (selectedQuickReward.hasStockControl !== false && 
        (selectedQuickReward.type === 'product' || selectedQuickReward.type === 'rental') &&
        quickRedemptionBatches.length > 0) {
      
      if (!selectedQuickBatch) {
        setError('Por favor, selecione um lote antes de confirmar o resgate.');
        return;
      }
    }

    try {
      setProcessingRedemption(selectedQuickReward.id);
      setError('');
      setShowQuickRedemptionModal(false);

      // Criar registro de resgate
      const { requestRedemption } = await import('../../services/rewardsService');
      const redemption = await requestRedemption(
        customer.id,
        user.companyId,
        selectedQuickReward.id,
        customer.points
      );

      // Marcar imediatamente como completado para descontar os pontos
      const { updateRedemptionStatus } = await import('../../services/rewardsService');
      
      // Incluir informação do lote selecionado se disponível
      const additionalData = {};
      if (selectedQuickBatch && quickRedemptionBatches.length > 0) {
        additionalData.selectedBatchCode = selectedQuickBatch;
      }
      
      // updateRedemptionStatus já chama pointsService.redeemPoints internamente,
      // que adiciona o registro no histórico. Não precisamos chamar addHistory novamente.
      await updateRedemptionStatus(
        redemption.id, 
        'completed', 
        `Resgate rápido processado pela empresa - ${selectedQuickReward.name}`,
        additionalData
      );

      // Atualizar dados do cliente
      const cleanCustomerDoc = customer.cpf.replace(/[^\d]/g, '');
      const updatedCustomers = await customerService.getByDocument(cleanCustomerDoc);
      const updatedCustomer = updatedCustomers.find(c => c.companyId === user.companyId);
      
      // Reset qualquer animação anterior
      resetAnimation();
      
      // Aguardar um pouco antes de mostrar a animação
      setTimeout(() => {
        const oldPoints = customer.points;
        const newPoints = updatedCustomer.points;
        showRewardAnimation(oldPoints, newPoints, selectedQuickReward.name, 3500);
      }, 100);
      
      setCustomer(updatedCustomer);

      // Limpar seleções
      setSelectedReward(null);
      setCustomPoints('');
      setNotes('');
      setSelectedQuickReward(null);
      setSelectedQuickBatch('');
      setQuickRedemptionBatches([]);

      // Mostrar modal de sucesso
      setSuccessData({
        rewardName: selectedQuickReward.name,
        pointsUsed: selectedQuickReward.pointsRequired,
        previousPoints: customer.points,
        newPoints: updatedCustomer.points
      });
      setShowSuccessModal(true);

    } catch (err) {
      setError('Erro ao processar resgate: ' + err.message);
    } finally {
      setProcessingRedemption(null);
    }
  };

  const handleForceRedemption = async () => {
    if (!customer || (!selectedReward && !customPoints)) {
      setError('Selecione um prêmio ou defina pontos personalizados');
      return;
    }

    const pointsToRedeem = selectedReward ? selectedReward.pointsRequired : parseInt(customPoints);
    
    if (isNaN(pointsToRedeem) || pointsToRedeem <= 0) {
      setError('Pontos inválidos');
      return;
    }

    if (customer.points < pointsToRedeem) {
      setError(`Cliente não possui pontos suficientes. Saldo atual: ${customer.points}`);
      return;
    }

    setShowModal(true);
  };

  const confirmForceRedemption = async () => {
    const pointsToRedeem = selectedReward ? selectedReward.pointsRequired : parseInt(customPoints);
    const rewardName = selectedReward ? selectedReward.name : 'Resgate Manual';

    try {
      setLoading(true);

      // Criar registro de resgate forçado
      const { requestRedemption } = await import('../../services/rewardsService');
      
      // Se for um prêmio existente, usar o fluxo normal
      if (selectedReward) {
        const redemption = await requestRedemption(
          customer.id,
          user.companyId,
          selectedReward.id,
          customer.points
        );

        // Marcar imediatamente como completado para descontar os pontos
        const { updateRedemptionStatus } = await import('../../services/rewardsService');
        await updateRedemptionStatus(redemption.id, 'completed', `Resgate forçado pela empresa. ${notes}`);
      } else {
        // Se for pontos personalizados, criar resgate manual
        const { createManualRedemption } = await import('../../services/rewardsService');
        
        // createManualRedemption já cria o resgate e desconta os pontos
        // Não precisamos fazer mais nada aqui
        await createManualRedemption(
          customer.id,
          user.companyId,
          pointsToRedeem,
          rewardName,
          notes
        );
      }

      // Atualizar dados do cliente
      const updatedCustomer = await customerService.getById(customer.id);
      setCustomer(updatedCustomer);

      // Mostrar modal de sucesso
      setSuccessData({
        rewardName: selectedReward ? selectedReward.name : 'Resgate Manual',
        pointsUsed: pointsToRedeem,
        previousPoints: customer.points,
        newPoints: updatedCustomer.points
      });
      setShowModal(false);
      setShowSuccessModal(true);
      
      // Limpar formulário
      setSelectedReward(null);
      setCustomPoints('');
      setNotes('');

    } catch (error) {
      alert('Erro ao processar resgate: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Minus className="mr-2 text-red-600" />
          Forçar Resgate de Pontos
        </h1>
        <p className="text-gray-600 mt-1">
          Desconte pontos do cliente diretamente (usar com cautela)
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
          <div className="flex items-start">
            <AlertTriangle className="text-yellow-600 mr-2 mt-0.5" size={16} />
            <div>
              <p className="text-yellow-800 font-medium">Atenção!</p>
              <p className="text-yellow-700 text-sm">
                Esta funcionalidade permite descontar pontos diretamente do cliente. 
                Use apenas quando necessário e sempre documente o motivo.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Busca por Cliente */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Buscar Cliente</h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CPF/CNPJ do Cliente
            </label>
            <input
              type="text"
              value={customerDocument}
              onChange={(e) => {
                // Permitir apenas números e limitar a 14 dígitos (CNPJ)
                const value = e.target.value.replace(/[^\d]/g, '').slice(0, 14);
                setCustomerDocument(value);
              }}
              placeholder="00000000000 (CPF) ou 00000000000000 (CNPJ)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && handleSearchCustomer()}
              maxLength="14"
            />
            <p className="text-xs text-gray-500 mt-1">
              Digite 11 números para CPF ou 14 números para CNPJ (sem formatação)
            </p>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearchCustomer}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Search size={16} />
              Buscar
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {successMessage}
        </div>
      )}

      {loading && <LoadingSpinner />}

      {customer && (
        <div className="space-y-6">
          {/* Informações do Cliente */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Cliente Encontrado</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Nome</p>
                <p className="font-medium">{customer.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">CPF/CNPJ</p>
                <p className="font-medium">{formatters.formatCpfOrCnpj(customer.cpf)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Pontos Atuais</p>
                <p className="font-bold text-2xl text-blue-600">{customer.points || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Ações</p>
                <button 
                  onClick={handleViewHistory}
                  disabled={loading}
                  className="text-blue-600 hover:text-blue-900 flex items-center gap-1 text-sm disabled:opacity-50"
                >
                  <Eye size={16} />
                  Ver Histórico
                </button>
              </div>
            </div>
          </div>

          {/* Opções de Resgate */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Opções de Resgate</h2>
            
            {/* Prêmios Cadastrados */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-800 mb-3">Prêmios Cadastrados</h3>
              {rewards.length === 0 ? (
                <p className="text-gray-600">Nenhum prêmio cadastrado</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rewards.map((reward) => (
                    <div
                      key={reward.id}
                      className={`border rounded-lg p-4 transition-all ${
                        selectedReward?.id === reward.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setSelectedReward(selectedReward?.id === reward.id ? null : reward);
                          setCustomPoints('');
                        }}
                      >
                        {/* Imagem do Prêmio */}
                        {reward.imageUrl && (
                          <div className="mb-3">
                            <img
                              src={reward.imageUrl}
                              alt={reward.name}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                          </div>
                        )}
                        
                        <h4 className="font-semibold text-gray-900">{reward.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{reward.description}</p>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-lg font-bold text-red-600">
                            -{reward.pointsRequired} pontos
                          </span>
                          {customer.points >= reward.pointsRequired ? (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              Disponível
                            </span>
                          ) : (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                              Insuficiente
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Botão de Resgate Rápido */}
                      {customer.points >= reward.pointsRequired && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickRedemption(reward);
                          }}
                          disabled={processingRedemption === reward.id}
                          className="w-full mt-2 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          {processingRedemption === reward.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              Processando...
                            </>
                          ) : (
                            <>
                              <Minus size={14} />
                              Resgatar Agora
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pontos Personalizados */}
            <div className="border-t pt-6">
              <h3 className="text-md font-medium text-gray-800 mb-3">Ou Definir Pontos Personalizados</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pontos a Descontar
                  </label>
                  <input
                    type="number"
                    value={customPoints}
                    onChange={(e) => {
                      setCustomPoints(e.target.value);
                      setSelectedReward(null);
                    }}
                    placeholder="Ex: 50"
                    min="1"
                    max={customer.points}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motivo (obrigatório)
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Ex: Produto entregue manualmente"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Botão de Ação */}
            <div className="flex justify-end mt-6">
              <button
                onClick={handleForceRedemption}
                disabled={loading || (!selectedReward && !customPoints) || !notes.trim()}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Minus size={16} />
                Processar Resgate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Confirmar Resgate Forçado"
      >
        {customer && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="text-yellow-600 mr-2 mt-0.5" size={16} />
                <div>
                  <p className="text-yellow-800 font-medium">Atenção: Esta ação não pode ser desfeita!</p>
                  <p className="text-yellow-700 text-sm">
                    Você está prestes a descontar pontos diretamente do cliente.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Cliente:</span>
                <span className="font-medium">{customer.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Item:</span>
                <span className="font-medium">
                  {selectedReward ? selectedReward.name : 'Resgate Manual'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pontos a descontar:</span>
                <span className="font-bold text-red-600">
                  -{selectedReward ? selectedReward.pointsRequired : customPoints} pontos
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pontos atuais:</span>
                <span className="font-medium">{customer.points}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pontos após resgate:</span>
                <span className="font-bold text-green-600">
                  {customer.points - (selectedReward ? selectedReward.pointsRequired : parseInt(customPoints || 0))} pontos
                </span>
              </div>
              {notes && (
                <div className="pt-2 border-t">
                  <span className="text-gray-600">Motivo:</span>
                  <p className="text-sm text-gray-800 mt-1">{notes}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={confirmForceRedemption}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Processando...' : 'Confirmar Resgate'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Histórico */}
      <Modal 
        isOpen={showHistoryModal} 
        onClose={() => setShowHistoryModal(false)}
        title={`Histórico de Pontos - ${customer?.name || ''}`}
      >
        <div className="max-h-96 overflow-y-auto">
          {customerHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhuma movimentação encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {customerHistory.map((history, index) => {
                const isRedemption = history.type === 'redeem' || history.points < 0;
                const pointsValue = Math.abs(history.points);
                const pointsColor = isRedemption ? 'text-red-600' : 'text-green-600';
                const pointsPrefix = isRedemption ? '-' : '+';
                
                return (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-300">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          isRedemption ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {isRedemption ? 'Resgate' : 'Adição'}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatDate(history.createdAt)}
                        </p>
                      </div>
                      <p className={`text-lg font-bold ${pointsColor}`}>
                        {pointsPrefix}{pointsValue} pontos
                      </p>
                    </div>
                    <p className="text-sm text-gray-700">
                      {history.reason || history.description || 'Sem descrição'}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={() => setShowHistoryModal(false)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Fechar
          </button>
        </div>
      </Modal>

      {/* Modal de Confirmação do Resgate Rápido */}
      <Modal
        isOpen={showQuickRedemptionModal}
        onClose={() => {
          setShowQuickRedemptionModal(false);
          setSelectedQuickReward(null);
        }}
        title="Confirmar Resgate Rápido"
      >
        {customer && selectedQuickReward && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="text-yellow-600 mr-2 mt-0.5" size={16} />
                <div>
                  <p className="text-yellow-800 font-medium">Atenção: Esta ação não pode ser desfeita!</p>
                  <p className="text-yellow-700 text-sm">
                    Você está prestes a descontar pontos diretamente do cliente.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Cliente:</span>
                <span className="font-medium">{customer.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prêmio:</span>
                <span className="font-medium">{selectedQuickReward.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pontos necessários:</span>
                <span className="font-bold text-red-600">
                  -{selectedQuickReward.pointsRequired} pontos
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pontos atuais:</span>
                <span className="font-medium">{customer.points}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pontos após resgate:</span>
                <span className="font-bold text-green-600">
                  {customer.points - selectedQuickReward.pointsRequired} pontos
                </span>
              </div>
            </div>

            {/* Seleção de Lote */}
            {selectedQuickReward && 
             selectedQuickReward.hasStockControl !== false && 
             (selectedQuickReward.type === 'product' || selectedQuickReward.type === 'rental') && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lote para débito {quickRedemptionBatches.length > 0 ? '*' : ''}
                </label>
                {loadingQuickBatches ? (
                  <div className="flex items-center justify-center p-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-600">Carregando lotes...</span>
                  </div>
                ) : quickRedemptionBatches.length > 0 ? (
                  <select
                    value={selectedQuickBatch}
                    onChange={(e) => setSelectedQuickBatch(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Selecione um lote</option>
                    {quickRedemptionBatches.map((batch) => (
                      <option key={batch.batchCode} value={batch.batchCode}>
                        {batch.batchCode} (Estoque: {batch.stockQuantity})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      ℹ️ Este produto não possui códigos de lote configurados. O resgate será processado normalmente.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setShowQuickRedemptionModal(false);
                  setSelectedQuickReward(null);
                  setSelectedQuickBatch('');
                  setQuickRedemptionBatches([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={confirmQuickRedemption}
                disabled={processingRedemption === selectedQuickReward.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {processingRedemption === selectedQuickReward.id ? 'Processando...' : 'Confirmar Resgate'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Sucesso */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title=""
      >
        {successData && (
          <div className="text-center py-6">
            {/* Ícone de Sucesso */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            
            {/* Título */}
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Resgate Concluído!
            </h3>
            
            {/* Nome do Prêmio */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-lg font-semibold text-blue-900">
                {successData.rewardName}
              </p>
            </div>
            
            {/* Informações de Pontos */}
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                <span className="text-gray-600">Pontos descontados:</span>
                <span className="text-xl font-bold text-red-600">
                  -{successData.pointsUsed}
                </span>
              </div>
              
              <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                <span className="text-gray-600">Saldo anterior:</span>
                <span className="text-lg font-semibold text-gray-700">
                  {successData.previousPoints}
                </span>
              </div>
              
              <div className="flex justify-between items-center bg-green-50 border-2 border-green-200 p-3 rounded-lg">
                <span className="text-gray-600 font-medium">Novo saldo:</span>
                <span className="text-2xl font-bold text-green-600">
                  {successData.newPoints}
                </span>
              </div>
            </div>
            
            {/* Botão de Fechar */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full px-6 py-3 bg-green-600 text-white text-lg font-semibold rounded-lg hover:bg-green-700 transition-colors"
            >
              OK, Entendi
            </button>
          </div>
        )}
      </Modal>

      {/* Animação do Slot Machine */}
      {animationData && (
        <SlotMachineAnimation
          oldPoints={animationData.oldPoints}
          newPoints={animationData.newPoints}
          rewardName={animationData.rewardName}
          onComplete={hideAnimation}
        />
      )}
    </div>
  );
};

export default ForceRedemption;
